import { EstimatedTradeCostRequest } from './EstimatedTradeCostRequest';
import { DateTime } from 'luxon';
import {
    EstimatedTradeCostApiRequest,
    EstimatedTradeCostApiRequestOrder,
} from 'widgets/estimatedTradeCost/api/EstimatedTradeCostApiRequest';
import { StrikeBenchmark } from 'widgets/estimatedTradeCost/components/options';
import { Time, TimeFormat } from '@instinet-analytics/core-lib';
import { EstimatedTradeCostConfigState } from 'widgets/estimatedTradeCost/widget/store/EstimatedTradeCostConfigState';
import { EstimatedTradeCostComponentState } from 'widgets/estimatedTradeCost/widget/store/componentSlice';
import Order from 'communication/types/order/Order';

export const strikeTimeToString = (t?: Time) =>
    t
        ? `${t.hours.toString().padStart(2, '0')}:${t.mins
              .toString()
              .padStart(2, '0')}`
        : '';

export const formatResponseTime = (
    timeFormat: TimeFormat,
    time: string | undefined
) => (time ? timeFormat.toTimeMinutes(new Date(time).getTime()) : '');

export const toApiRequest: (
    request: EstimatedTradeCostRequest
) => EstimatedTradeCostApiRequest = ({
    orderId,
    inetSymbol,
    orderQtyRemaining,
    specifiedQty,
    profile,
    benchmark,
    minPartLit,
    maxPartLit,
    strikeTime,
}) => {
    const startTime = DateTime.now().toFormat('HH:mm');
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const qty = specifiedQty ?? orderQtyRemaining ?? 0;

    const order: EstimatedTradeCostApiRequestOrder = {
        id: orderId,
        sym: inetSymbol,
        qty,
    };

    if (benchmark === StrikeBenchmark && strikeTime) {
        order.strikeTime = strikeTimeToString(strikeTime);
    }

    return {
        orders: [order],
        strategies: [
            {
                benchmark,
                profile,
                startTime: startTime,
                timezone,
                minPartLit,
                maxPartLit,
            },
        ],
    };
};

export const toRequest: (
    order: Order | undefined,
    config: EstimatedTradeCostConfigState,
    componentState: EstimatedTradeCostComponentState | undefined
) => EstimatedTradeCostRequest | undefined = (
    order,
    config,
    componentState
) => {
    if (!order) {
        return undefined;
    }

    const {
        orderID: orderId,
        rem: orderQtyRemaining,
        isymbol: inetSymbol,
    } = order;

    const specifiedQty = componentState?.qty;

    const { benchmark, profile, minPartLit, maxPartLit, strikeTime } = config;

    if (!benchmark || !profile) {
        return undefined;
    }

    const request: EstimatedTradeCostRequest = {
        orderId,
        orderQtyRemaining: orderQtyRemaining ?? 0,
        specifiedQty,
        inetSymbol,
        benchmark,
        profile,
        minPartLit: minPartLit ?? 0,
        maxPartLit: maxPartLit ?? 0,
    };

    if (benchmark === StrikeBenchmark) {
        request.strikeTime = strikeTime;
    }

    return request;
};
